import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { CheckCircle2, Clock, Download, FileQuestion, ShieldCheck, XCircle } from 'lucide-react'
import { useState } from 'react'
import { get } from '../lib/api.ts'
import { fmtDate, fmtDateTime } from '../lib/format.ts'
import { Spinner } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'

interface Proof { status: 'pendente' | 'confirmado'; bitcoinBlock: number | null; bitcoinTime: string | null; submittedAt: string | null; proof: boolean }
interface V { condoName: string; title: string; category: string; docDate: string | null; version: number; kind: string; sha256: string; registeredAt: string; isCurrent: boolean; pdfSignature: string | null; signers: { name: string; role: string; at: string }[]; timestamp: Proof | null }

/** Proof of time (ADR 0044): what the OpenTimestamps proof says, how to check it without us, and what it is not. */
function ProofOfTime({ code, p }: { code: string; p: Proof }) {
  const confirmed = p.status === 'confirmado' && p.bitcoinBlock !== null
  return (
    <section className="mt-6 rounded-ficha border border-pauta bg-folha p-5">
      <h2 className="flex items-center gap-2 font-bold">{confirmed ? <ShieldCheck className="size-5 text-carimbo" /> : <Clock className="size-5 text-tinta-2" />}{' '}{t('Prova de data')}</h2>
      {confirmed ? (
        <p className="mt-2">
          {p.bitcoinTime ? t('Registrado no Bitcoin no bloco {block}, de {date}.', { block: p.bitcoinBlock!, date: fmtDateTime(p.bitcoinTime) }) : t('Registrado no Bitcoin no bloco {block}.', { block: p.bitcoinBlock! })}{' '}
          {t('Isso prova que este arquivo já existia nessa data, e qualquer pessoa pode conferir sem depender do Subsíndico.')}
        </p>
      ) : (
        <p className="mt-2">
          {p.submittedAt ? t('Em andamento: o resumo (SHA-256) deste arquivo foi enviado aos calendários OpenTimestamps em {date}. A confirmação no Bitcoin costuma levar algumas horas.', { date: fmtDateTime(p.submittedAt) }) : t('Em andamento: o resumo (SHA-256) deste arquivo será enviado aos calendários OpenTimestamps nos próximos minutos.')}
        </p>
      )}
      {p.proof && (
        <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <a href={`/api/verify/${encodeURIComponent(code)}/ots`} download className="inline-flex items-center gap-1.5 font-semibold text-caneta underline"><Download className="size-4" />{t('Baixar a prova (.ots)')}</a>
          <a href="https://opentimestamps.org" target="_blank" rel="noreferrer" className="font-semibold text-caneta underline">{t('Conferir em opentimestamps.org')}</a>
        </p>
      )}
      <p className="mt-3 text-sm text-tinta-2">{t('Só o resumo do arquivo sai do Subsíndico, nunca o conteúdo. A prova de data não é assinatura nem carimbo do tempo ICP-Brasil.')}</p>
    </section>
  )
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function VerifyPage() {
  const { code } = useParams({ strict: false }) as { code: string }
  const q = useQuery({ queryKey: ['verify', code], queryFn: () => get<V>(`/api/verify/${encodeURIComponent(code)}`), retry: false })
  const [check, setCheck] = useState<'idle' | 'busy' | 'match' | 'nomatch'>('idle')
  return (
    <main className="mx-auto max-w-2xl px-5 py-10">
      <p className="font-semibold text-caneta">{t('Subsíndico')}</p>
      <h1 className="mt-2 text-[1.8rem] font-bold">{t('Verificação de documento')}</h1>
      {q.isLoading ? <Spinner /> : q.error ? (
        <div className="mt-6 flex items-start gap-3 rounded-ficha border border-margem/30 bg-folha p-5">
          <FileQuestion className="size-6 shrink-0 text-margem" />
          <div><p className="font-semibold">{t('Código não encontrado')}</p><p className="text-tinta-2">{t('Confira o código impresso no documento. Documentos excluídos do acervo deixam de ser verificáveis.')}</p></div>
        </div>
      ) : (
        <>
          <section className="mt-6 rounded-ficha border border-pauta bg-folha p-5">
            <p className="flex items-center gap-2 text-carimbo"><ShieldCheck className="size-5" /> <span className="font-semibold">{t('Registrado no Subsíndico')}</span></p>
            <p className="mt-3 font-doc text-[1.35rem] font-semibold leading-snug">{q.data!.title}</p>
            <p className="mt-1 text-tinta-2">{q.data!.condoName}{q.data!.docDate ? `, ${fmtDate(q.data!.docDate)}` : ''}</p>
            <dl className="mt-4 grid gap-x-6 gap-y-2 text-[0.95rem] sm:grid-cols-[10rem_1fr]">
              <dt className="text-tinta-2">{t('Registrado em')}</dt><dd>{fmtDateTime(q.data!.registeredAt)}{' '}{t('(versão')}{' '}{q.data!.version}{q.data!.kind === 'signed' ? t(', via assinada') : ''})</dd>
              <dt className="text-tinta-2">{t('Situação')}</dt><dd>{q.data!.isCurrent ? t('É a versão vigente') : t('Existe uma versão mais nova deste documento')}</dd>
              {q.data!.pdfSignature === 'present' && (<><dt className="text-tinta-2">{t('Assinatura digital')}</dt><dd>{t('O PDF contém assinatura digital. Valide em validar.iti.gov.br.')}</dd></>)}
              <dt className="text-tinta-2">{t('SHA-256')}</dt><dd className="break-all font-doc text-[0.85rem]">{q.data!.sha256}</dd>
            </dl>
            {q.data!.signers.length > 0 && (
              <>
                <h2 className="mt-5 font-bold">{t('Assinado eletronicamente por')}</h2>
                <ul className="mt-1">{q.data!.signers.map((s, i) => <li key={i}>{s.name} <span className="text-tinta-2">({s.role}, {fmtDateTime(s.at)})</span></li>)}</ul>
              </>
            )}
          </section>
          {q.data!.timestamp && <ProofOfTime code={code} p={q.data!.timestamp} />}
          <section className="mt-6 rounded-ficha border border-dashed border-caneta/50 bg-folha p-5">
            <h2 className="font-bold">{t('Este arquivo é o mesmo?')}</h2>
            <p className="mt-1 text-tinta-2">{t('Escolha o PDF que você recebeu. A comparação é feita no seu aparelho; o arquivo não é enviado a lugar nenhum.')}</p>
            <input className="mt-3" type="file" aria-label={t('Arquivo para comparar')} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setCheck('busy'); setCheck((await sha256Hex(f)) === q.data!.sha256 ? 'match' : 'nomatch') }} />
            {check === 'match' && <p className="mt-3 flex items-center gap-2 font-semibold text-carimbo"><CheckCircle2 className="size-5" />{' '}{t('Idêntico ao documento registrado.')}</p>}
            {check === 'nomatch' && <p className="mt-3 flex items-center gap-2 font-semibold text-margem"><XCircle className="size-5" />{' '}{t('Diferente do documento registrado. O arquivo foi alterado ou é outra versão.')}</p>}
          </section>
        </>
      )}
    </main>
  )
}
